/**
 * The one sentence a failed request becomes, wherever it is shown.
 *
 * Shared by the upload runners and the editors in the detail overlay, and kept
 * free of either so that showing an error never loads an upload runner.
 */

/**
 * GCS rejections arrive as XML, not as a FastAPI `detail`.
 *
 * A signed-URL PUT is the one call in this flow that does not go to our own
 * backend, and it is also the most likely to fail — an expired signature, a
 * missing CORS rule and a Content-Type mismatch all land here. Left to axios
 * they render as "Request failed with status code 403", which tells the user
 * nothing and tells whoever they report it to even less.
 */
function describeGcsError(data: unknown): string | null {
  if (typeof data !== 'string') return null;
  const code = /<Code>([^<]+)<\/Code>/.exec(data)?.[1];
  if (!code) return null;

  // Only the codes a user can actually act on are translated; the rest are
  // passed through verbatim so the raw GCS code reaches the bug report.
  if (code === 'ExpiredToken' || code === 'RequestExpired') {
    return 'The upload link expired before this file was reached — upload the remaining files as a smaller batch.';
  }
  if (code === 'SignatureDoesNotMatch') {
    return 'The upload link was rejected by storage (signature mismatch).';
  }
  return `Storage rejected the upload (${code}).`;
}

/** Prefer the backend's `detail`, which carries the real reason (bad magic number, etc.). */
export function describeError(error: unknown, fallback: string): string {
  if (typeof error === 'object' && error !== null && 'response' in error) {
    const response = (error as { response?: { data?: unknown } }).response;
    const data = response?.data;
    if (typeof data === 'object' && data !== null) {
      if ('detail' in data) {
        const detail = (data as { detail: unknown }).detail;
        if (typeof detail === 'string') return detail;
        if (typeof detail === 'object' && detail !== null) {
          if ('message' in detail && typeof (detail as { message: unknown }).message === 'string') {
            return (detail as { message: string }).message;
          }
          if (Array.isArray(detail)) {
            const firstMsg = detail[0]?.msg || detail[0]?.message;
            if (typeof firstMsg === 'string') return firstMsg;
          }
          return JSON.stringify(detail);
        }
      }
      if ('message' in data && typeof (data as { message: unknown }).message === 'string') {
        return (data as { message: string }).message;
      }
    }

    const gcs = describeGcsError(response?.data);
    if (gcs) return gcs;
  }
  if (error instanceof Error && error.message) return error.message;
  return fallback;
}
